// PoLineEditor — one editable budget-line row inside PurchaseOrderFormModal.
// Extracted so the modal stays holdable. Presentational: it renders the draft
// line and reports edits via onChange; the auto-total invariant (unit price ×
// quantity) lives in poFormState.updateLine, which the modal applies. Money
// fields are text inputs (inputMode decimal) so a half-typed value is a valid
// interim state. Each visible <label> is associated to its input via
// htmlFor/id (scoped to the row's stable client key) so the label is a real
// programmatic association, not just a same-text aria-label; aria-label is
// kept alongside so existing accessible-name queries are unaffected.
//
// The row has a persistent header — a toggle button (same DOM node whether
// collapsed or expanded) plus, only while collapsed, a summary of the number/
// type/description and total and, on error, a visible "Error" label (never
// colour alone). Expanding replaces the summary with the full editable
// content below the header. A per-line validation error also marks the
// specific failing control (aria-invalid + aria-describedby), not the whole
// row, using the `errorField` the caller derives from poFormState's
// `lineErrorFields`.
//
// Vertical order inside the expanded detail: header fields (line number/
// type/description/GST) → tags → selected-value breakdown → pricing fields
// (unit price/quantity/line total/remove) → adjustments ledger. Tags and the
// breakdown sit ahead of pricing so the user sees what a line is tagged to,
// and what that implies value-wise, before keying in a price — the breakdown
// is a guide, not a binding source for the line total.
//
// A line carries no single item/SOW/milestone reference — it tags zero or
// more rate-card items, milestones and SOWs via TagMultiSelect (option lists
// threaded down from the modal, ultimately sourced from finance setup/summary
// and tracking data; `usedElsewhere` greys — but never locks — ids the PO's
// other lines already tag, since a variation can split a milestone or SOW
// across lines), plus its own GST-applies flag and a manual adjustments
// ledger (LineAdjustmentsEditor) for opening balances and SAP true-ups. The
// breakdown itself is derived (not stored) via the pure sumSelectedTagValues,
// concatenating all three tag lists/selections (milestones, then SOWs, then
// rate items) since their id namespaces are disjoint by construction. The
// breakdown shows each lump-sum tag at its full value even when another
// line shares it, so it names those shared tags and asks for this line's
// share — the reconciliation check against the PO total is the hard stop,
// this note is the early one.

import { AlertCircle, ChevronDown, ChevronRight, Trash2 } from "lucide-react";
import CurrencyInput from "../../common/CurrencyInput";
import NumberInput from "../../common/NumberInput";
import TagMultiSelect, { type TagOption } from "./TagMultiSelect";
import LineAdjustmentsEditor from "./LineAdjustmentsEditor";
import { sumSelectedTagValues } from "./poLineValue";
import { PO_LINE_TYPES, type PoLineType } from "../../../types/finance";
import { formatAud } from "../../../utils/financeCalc";
import {
  parseMoney,
  type PoLineDraft,
  type PoLineErrorField,
  type TagsUsedOnOtherLines,
} from "./poFormState";

const LINE_TYPE_LABEL: Record<PoLineType, string> = {
  standard: "Standard",
  contingency: "Contingency",
  other: "Other",
};

interface SharedLumpSumNote {
  id: string;
  label: string;
  hint: string;
}

// Lump-sum tags (milestones, SOWs) selected on this line that another line
// of the PO also tags, with the owning-line hint — the breakdown counts each
// at full value, so the editor asks for this line's share instead.
function sharedLumpSumNotes(
  families: Array<[TagOption[], string[], Map<string, string> | undefined]>,
): SharedLumpSumNote[] {
  const notes: SharedLumpSumNote[] = [];
  for (const [options, selectedIds, usedOnOtherLines] of families) {
    if (!usedOnOtherLines) continue;
    for (const id of selectedIds) {
      const hint = usedOnOtherLines.get(id);
      if (hint === undefined) continue;
      const label = options.find((o) => o.id === id)?.label ?? id;
      notes.push({ id, label, hint });
    }
  }
  return notes;
}

interface PoLineEditorProps {
  line: PoLineDraft;
  error?: string;
  /** Which control `error` is about, when known — marks that single input as
   *  invalid rather than the whole row. */
  errorField?: PoLineErrorField;
  onChange: (patch: Partial<PoLineDraft>) => void;
  onRemove: () => void;
  canRemove: boolean;
  disabled?: boolean;
  rateItemOptions: TagOption[];
  milestoneOptions: TagOption[];
  sowOptions: TagOption[];
  /** Tag ids already used by the PO's other budget lines (id → owning-line
   *  hint), per family — greyed but still tickable in this row's pickers. */
  usedElsewhere?: TagsUsedOnOtherLines;
  /** Whether the row shows only its persistent header + summary, or the full
   *  editable detail. */
  collapsed: boolean;
  onToggleCollapsed: () => void;
}

// Field ids scoped to the row's stable client key (line.key), so the visible
// <label> elements are genuinely associated with their inputs (htmlFor/id)
// rather than relying on aria-label alone — required for the label to be
// clickable/focus-targeting and for the row to remain unambiguous in a
// dynamic list of lines. Exported so callers can address a specific failing
// control (e.g. poLineFieldId(line.key, errorField)).
// eslint-disable-next-line react-refresh/only-export-components
export function poLineFieldId(rowKey: string, field: string): string {
  return `${rowKey}-${field}`;
}

export default function PoLineEditor({
  line,
  error,
  errorField,
  onChange,
  onRemove,
  canRemove,
  disabled,
  rateItemOptions,
  milestoneOptions,
  sowOptions,
  usedElsewhere,
  collapsed,
  onToggleCollapsed,
}: PoLineEditorProps) {
  const id = (field: string) => poLineFieldId(line.key, field);
  // Marks the one control `errorField` names as invalid, describing it with
  // the same error text the bottom paragraph shows — never the whole row.
  const errorProps = (field: PoLineErrorField) =>
    error && errorField === field
      ? { "aria-invalid": true, "aria-describedby": id("error") }
      : {};
  const collapsedTotal = parseMoney(line.lineTotal);
  const lineLabel = `Line ${line.lineNumber.trim() || "—"}`;
  return (
    <div
      data-testid="po-line-row"
      className="rounded-lg border border-slate-200 bg-slate-50/60 p-3 dark:border-slate-700 dark:bg-slate-900/40"
    >
      <div className="flex items-center justify-between gap-3">
        <button
          type="button"
          onClick={onToggleCollapsed}
          disabled={disabled}
          aria-expanded={!collapsed}
          aria-controls={collapsed ? undefined : id("detail")}
          aria-describedby={
            collapsed && error ? id("collapsed-error") : undefined
          }
          className="flex min-h-11 min-w-0 flex-1 items-center gap-2 text-left text-body-sm font-medium text-slate-700 hover:text-slate-900 dark:text-slate-300 dark:hover:text-slate-100"
        >
          {collapsed ? (
            <ChevronRight className="h-4 w-4 shrink-0" aria-hidden="true" />
          ) : (
            <ChevronDown className="h-4 w-4 shrink-0" aria-hidden="true" />
          )}
          <span className="truncate">
            {collapsed
              ? [
                  lineLabel,
                  LINE_TYPE_LABEL[line.lineType],
                  line.description.trim(),
                ]
                  .filter(Boolean)
                  .join(" · ")
              : lineLabel}
          </span>
        </button>
        {collapsed && (
          <span className="flex shrink-0 items-center gap-3">
            {error && (
              <>
                <span className="flex items-center gap-1 text-caption font-medium text-red-600 dark:text-red-400">
                  <AlertCircle className="h-3.5 w-3.5" aria-hidden="true" />
                  Error
                </span>
                <span id={id("collapsed-error")} className="sr-only">
                  {error}
                </span>
              </>
            )}
            <span className="tabular-nums text-body-sm text-slate-800 dark:text-slate-200">
              {collapsedTotal !== null ? formatAud(collapsedTotal) : "—"}
            </span>
          </span>
        )}
      </div>
      {!collapsed && (
        <div id={id("detail")}>
          <div className="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-12">
            <div className="sm:col-span-2">
              <label className="label" htmlFor={id("line-number")}>
                Line number
              </label>
              <input
                id={id("line-number")}
                type="text"
                aria-label="Line number"
                className="input-sm w-full"
                value={line.lineNumber}
                onChange={(e) => onChange({ lineNumber: e.target.value })}
                disabled={disabled}
                {...errorProps("line-number")}
              />
            </div>
            <div className="sm:col-span-3">
              <label className="label" htmlFor={id("line-type")}>
                Type
              </label>
              <select
                id={id("line-type")}
                aria-label="Line type"
                className="input-sm w-full"
                value={line.lineType}
                onChange={(e) =>
                  onChange({ lineType: e.target.value as PoLineType })
                }
                disabled={disabled}
              >
                {PO_LINE_TYPES.map((t) => (
                  <option key={t} value={t}>
                    {LINE_TYPE_LABEL[t]}
                  </option>
                ))}
              </select>
            </div>
            <div className="sm:col-span-5">
              <label className="label" htmlFor={id("description")}>
                Description
              </label>
              <input
                id={id("description")}
                type="text"
                aria-label="Line description"
                className="input-sm w-full"
                value={line.description}
                onChange={(e) => onChange({ description: e.target.value })}
                disabled={disabled}
                {...errorProps("description")}
              />
            </div>
            <div className="flex items-end sm:col-span-2">
              <label
                className="flex items-center gap-2 text-body-sm text-slate-700 dark:text-slate-300"
                htmlFor={id("gst-applies")}
              >
                <input
                  id={id("gst-applies")}
                  type="checkbox"
                  aria-label="GST applies"
                  className="h-4 w-4 rounded-sm border-slate-300 text-brand-600 focus:ring-brand-400 dark:border-slate-600 dark:bg-slate-900"
                  checked={line.gstApplies}
                  onChange={(e) => onChange({ gstApplies: e.target.checked })}
                  disabled={disabled}
                />
                GST applies
              </label>
            </div>
          </div>

          {/* Dense flow + col-span-full panels: each TagMultiSelect contributes
          its trigger cell and, when open, a full-width checklist row. Dense
          placement backfills the top row, so a later trigger (e.g. SOWs)
          stays beside its siblings while an earlier picker's panel occupies
          the row below — full-width panels keep long milestone/SOW labels
          on one readable line instead of wrapping inside a third-width
          column. */}
          <div className="mt-3 grid grid-flow-row-dense grid-cols-1 gap-3 sm:grid-cols-3">
            <TagMultiSelect
              label="Rate items"
              options={rateItemOptions}
              selectedIds={line.rateItemIds}
              onChange={(ids) => onChange({ rateItemIds: ids })}
              disabled={disabled}
              usedElsewhere={usedElsewhere?.rateItem}
              panelClassName="col-span-full"
            />
            <TagMultiSelect
              label="Milestones"
              options={milestoneOptions}
              selectedIds={line.milestoneIds}
              onChange={(ids) => onChange({ milestoneIds: ids })}
              disabled={disabled}
              usedElsewhere={usedElsewhere?.milestone}
              panelClassName="col-span-full"
            />
            <TagMultiSelect
              label="SOWs"
              options={sowOptions}
              selectedIds={line.sowIds}
              onChange={(ids) => onChange({ sowIds: ids })}
              disabled={disabled}
              usedElsewhere={usedElsewhere?.sow}
              panelClassName="col-span-full"
            />
          </div>

          {(() => {
            const summary = sumSelectedTagValues(
              [...milestoneOptions, ...sowOptions, ...rateItemOptions],
              [...line.milestoneIds, ...line.sowIds, ...line.rateItemIds],
            );
            if (summary.items.length === 0) return null;
            const sharedLumpSums = sharedLumpSumNotes([
              [milestoneOptions, line.milestoneIds, usedElsewhere?.milestone],
              [sowOptions, line.sowIds, usedElsewhere?.sow],
            ]);
            return (
              <div
                data-testid="po-line-breakdown"
                className="mt-3 rounded-md border border-slate-200 bg-white p-3 text-body-sm dark:border-slate-700 dark:bg-slate-900/40"
              >
                <ul className="space-y-1">
                  {summary.items.map((it, i) => (
                    <li
                      key={i}
                      className="flex items-center justify-between gap-3 tabular-nums"
                    >
                      <span className="text-slate-600 dark:text-slate-400">
                        {it.label}
                        {it.kind === "unitRate" && (
                          <span className="text-slate-400 dark:text-slate-500">
                            {" "}
                            (unit price)
                          </span>
                        )}
                      </span>
                      <span className="text-slate-800 dark:text-slate-200">
                        {it.value === null ? "— variable" : formatAud(it.value)}
                      </span>
                    </li>
                  ))}
                </ul>
                <div className="mt-2 flex items-center justify-between gap-3 border-t border-slate-200 pt-2 tabular-nums dark:border-slate-700">
                  <span className="font-medium text-slate-700 dark:text-slate-300">
                    Total selected value (ex-GST)
                  </span>
                  <span className="font-semibold text-slate-900 dark:text-slate-100">
                    {formatAud(summary.total)}
                  </span>
                </div>
                {summary.includesUnitRate && (
                  <p className="mt-1 text-caption text-slate-400 dark:text-slate-500">
                    Includes rate items at their per-unit rate.
                  </p>
                )}
                {summary.hasVariable && (
                  <p className="mt-1 text-caption text-slate-400 dark:text-slate-500">
                    Some selected items are variable-priced.
                  </p>
                )}
                {sharedLumpSums.map((note) => (
                  <p
                    key={note.id}
                    className="mt-1 text-caption text-amber-700 dark:text-amber-400"
                  >
                    {note.label} is also tagged on {note.hint} — enter only this
                    line&apos;s share in the line total.
                  </p>
                ))}
              </div>
            );
          })()}

          <div className="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-12">
            <div className="sm:col-span-3">
              <label className="label" htmlFor={id("unit-price")}>
                Unit price
              </label>
              <CurrencyInput
                id={id("unit-price")}
                size="sm"
                ariaLabel="Unit price"
                className="w-full"
                value={line.unitPrice}
                onChange={(raw) => onChange({ unitPrice: raw })}
                disabled={disabled}
              />
            </div>
            <div className="sm:col-span-3">
              <label className="label" htmlFor={id("quantity")}>
                Quantity
              </label>
              <NumberInput
                id={id("quantity")}
                size="sm"
                ariaLabel="Quantity"
                className="w-full"
                value={line.quantity}
                onChange={(raw) => onChange({ quantity: raw })}
                disabled={disabled}
              />
            </div>
            <div className="sm:col-span-3">
              <label className="label" htmlFor={id("line-total")}>
                Line total
              </label>
              <CurrencyInput
                id={id("line-total")}
                size="sm"
                ariaLabel="Line total"
                className="w-full"
                value={line.lineTotal}
                onChange={(raw) => onChange({ lineTotal: raw })}
                disabled={disabled}
                ariaInvalid={!!error && errorField === "line-total"}
                ariaDescribedBy={
                  error && errorField === "line-total" ? id("error") : undefined
                }
              />
            </div>
            <div className="flex items-end justify-end sm:col-span-3">
              {canRemove && (
                <button
                  type="button"
                  aria-label="Remove line"
                  onClick={onRemove}
                  disabled={disabled}
                  className="btn-icon-ghost btn-icon-sm text-slate-500 hover:text-red-600 dark:text-slate-400 dark:hover:text-red-400"
                >
                  <Trash2 className="h-4 w-4" aria-hidden="true" />
                </button>
              )}
            </div>
          </div>

          <div className="mt-3">
            <LineAdjustmentsEditor
              adjustments={line.adjustments}
              onChange={(next) => onChange({ adjustments: next })}
              disabled={disabled}
            />
          </div>

          {error && (
            <p
              id={id("error")}
              className="mt-2 text-caption text-red-600 dark:text-red-400"
            >
              {error}
            </p>
          )}
        </div>
      )}
    </div>
  );
}
